import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { authApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useT } from '@/i18n'

/**
 * 点开邮件里的链接落到这里，自动提交令牌。
 *
 * 不要求登录：用户很可能是在手机上点开的，那台设备没登录过。
 */
export function VerifyEmailPage() {
  const t = useT()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const client = useQueryClient()

  const verify = useMutation({
    mutationFn: () => authApi.verifyEmail(token),
    // 验证成功后当前设备的 email_verified 要跟着变
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me() }),
  })

  // StrictMode 下 effect 会跑两次，用 ref 挡住重复提交
  const submitted = useRef(false)
  useEffect(() => {
    if (!token || submitted.current) return
    submitted.current = true
    verify.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (!token) {
    return <Result title={t('verifyEmail.incomplete')} detail={t('verifyEmail.incompleteBody')} />
  }
  if (verify.isPending || verify.isIdle) {
    return (
      <div className="py-12">
        <CenteredSpinner />
        <p className="mt-4 text-center text-[15px] text-ink-muted">{t('verifyEmail.verifying')}</p>
      </div>
    )
  }
  if (verify.isError) {
    const message =
      verify.error instanceof ApiError ? verify.error.message : t('verifyEmail.failedBody')
    return <Result title={t('verifyEmail.failed')} detail={message} />
  }
  return (
    <Result
      title={t('verifyEmail.done')}
      detail={t('verifyEmail.doneBody', { username: verify.data.username })}
    />
  )
}

function Result({ title, detail }: { title: string; detail: string }) {
  const t = useT()
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-extrabold">{title}</h1>
      <p className="text-[15px] text-ink-muted">{detail}</p>
      <Link to="/" className="inline-block text-brand-ink hover:underline">
        {t('verifyEmail.home')}
      </Link>
    </div>
  )
}
